import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A rounded square holding a single icon. Appearance is `.icon-tile` in
 * `styles/ui.css`, including the rule that the icon is always half the tile.
 *
 * That ratio is why the sizes are a closed set rather than a number prop: a 24px
 * icon in a 36px square looks cramped and a 16px icon in a 56px square looks
 * lost, and neither is visible until the two sit on the same page. Call sites
 * pass a bare `<Icon />` and the stylesheet sizes it, so getting it wrong is not
 * expressible.
 */

type Tone = "accent" | "neutral" | "ok" | "caution" | "stop" | "advisory" | "panel";
type Size = "sm" | "md" | "lg" | "xl" | "2xl";

export function IconTile({
  tone = "accent",
  size = "md",
  children,
  className,
}: {
  tone?: Tone;
  size?: Size;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // aria-hidden because a tile is decoration in every current use: the icon
    // restates a heading that is already next to it. Anything that genuinely
    // carries meaning on its own is a Badge, which has a text label.
    <span
      aria-hidden
      className={cn("icon-tile", `icon-tile--${tone}`, `icon-tile--${size}`, className)}
    >
      {children}
    </span>
  );
}
